import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
  Pin,
  type MapEvent,
} from "@vis.gl/react-google-maps";
import type { HoverSource, RoamEasyPlace } from "../routes/Explore";
import PlaceCard from "./PlaceCard";
import "./GoogleMaps.css";

const FALLBACK_CENTER: google.maps.LatLngLiteral = {
  lat: 47.6097,
  lng: -122.3331,
};

const INFO_WINDOW_GAP_PX = 68;
const INFO_WINDOW_ESTIMATED_HEIGHT = 180;
const MIN_LAT_MARGIN_DEG = 0.02;
const MIN_LNG_MARGIN_DEG = 0.02;
const SINGLE_PLACE_FOCUS_ZOOM = 1;
const HOVER_CLEAR_DELAY_MS = 240;

const getAngularLngDistance = (a: number, b: number) => {
  const diff = ((a - b + 540) % 360) - 180;
  return Math.abs(diff);
};

const getLngSpan = (east: number, west: number) => {
  const span = getAngularLngDistance(east, west);
  return span === 0 ? 360 : span;
};

const hasLocation = (
  place: RoamEasyPlace
): place is RoamEasyPlace & { location: google.maps.LatLngLiteral } =>
  Boolean(place.location);

export interface MapViewport {
  center: google.maps.LatLngLiteral;
  bounds: google.maps.LatLngBoundsLiteral | null;
  zoom: number;
}

interface GoogleMapsProps {
  places: RoamEasyPlace[];
  activePlaceId: string | null;
  infoWindowPlaceId?: string | null;
  onPlaceHover?: (placeId: string | null, source?: HoverSource) => void;
  onPlaceSelect?: (placeId: string | null) => void;
  onViewportChanged?: (viewport: MapViewport) => void;
  viewportResetKey?: number;
}

export default function GoogleMaps({
  places,
  activePlaceId,
  infoWindowPlaceId = null,
  onPlaceHover,
  onPlaceSelect,
  onViewportChanged,
  viewportResetKey = 0,
}: GoogleMapsProps) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY as string | undefined;
  const [viewportBounds, setViewportBounds] =
    useState<google.maps.LatLngBoundsLiteral | null>(null);
  const [infoWindowHeight, setInfoWindowHeight] = useState(
    INFO_WINDOW_ESTIMATED_HEIGHT
  );
  const infoWindowContentRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const lastAutoFitKeyRef = useRef<number | null>(null);
  const hoverClearTimeoutRef = useRef<number | null>(null);

  const clearScheduledHover = useCallback(() => {
    if (hoverClearTimeoutRef.current) {
      window.clearTimeout(hoverClearTimeoutRef.current);
      hoverClearTimeoutRef.current = null;
    }
  }, []);

  const requestHoverClear = useCallback(() => {
    clearScheduledHover();
    hoverClearTimeoutRef.current = window.setTimeout(() => {
      onPlaceHover?.(null, "map");
      hoverClearTimeoutRef.current = null;
    }, HOVER_CLEAR_DELAY_MS);
  }, [clearScheduledHover, onPlaceHover]);

  useEffect(() => {
    return () => {
      clearScheduledHover();
    };
  }, [clearScheduledHover]);

  const placesWithLocations = useMemo(() => places.filter(hasLocation), [places]);

  const initialCenter = useMemo(
    () => placesWithLocations[0]?.location ?? FALLBACK_CENTER,
    [placesWithLocations]
  );

  const infoWindowPlace = useMemo(() => {
    if (infoWindowPlaceId) {
      return placesWithLocations.find((p) => p.id === infoWindowPlaceId) ?? null;
    }
    return null;
  }, [infoWindowPlaceId, placesWithLocations]);

  useEffect(() => {
    if (!isMapReady) {
      return;
    }
    const map = mapInstanceRef.current;
    if (!map) {
      return;
    }
    if (!placesWithLocations.length) {
      lastAutoFitKeyRef.current = viewportResetKey;
      return;
    }
    if (viewportResetKey === lastAutoFitKeyRef.current) {
      return;
    }
    lastAutoFitKeyRef.current = viewportResetKey;
    if (placesWithLocations.length === 1) {
      const singleLocation = placesWithLocations[0].location!;
      map.moveCamera({
        center: singleLocation,
        zoom: Math.max(map.getZoom() ?? SINGLE_PLACE_FOCUS_ZOOM, SINGLE_PLACE_FOCUS_ZOOM),
      });
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    placesWithLocations.forEach((place) => {
      if (place.location) {
        bounds.extend(place.location);
      }
    });
    if (bounds.isEmpty()) {
      return;
    }
    map.fitBounds(bounds, { top: 64, right: 64, bottom: 64, left: 64 });
  }, [isMapReady, placesWithLocations, viewportResetKey]);

  useLayoutEffect(() => {
    const node = infoWindowContentRef.current;
    if (!node) {
      return;
    }
    if (typeof ResizeObserver === "undefined") {
      setInfoWindowHeight(node.getBoundingClientRect().height);
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setInfoWindowHeight(entry.contentRect.height);
      }
    });
    observer.observe(node);
    setInfoWindowHeight(node.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, [infoWindowPlace]);

  const infoWindowOffset = useMemo<[number, number]>(() => {
    if (!infoWindowPlace) {
      return [0, -INFO_WINDOW_GAP_PX];
    }
    if (!viewportBounds) {
      return [0, -INFO_WINDOW_GAP_PX];
    }
    const { lat, lng } = infoWindowPlace.location;
    const { north, south, east, west } = viewportBounds;
    const verticalSpan = Math.abs(north - south);
    const verticalMargin = Math.max(verticalSpan * 0.15, MIN_LAT_MARGIN_DEG);
    const measuredHeight =
      infoWindowContentRef.current?.offsetHeight ??
      infoWindowHeight ??
      INFO_WINDOW_ESTIMATED_HEIGHT;
    let verticalOffset = -INFO_WINDOW_GAP_PX;
    if (north - lat < verticalMargin) {
      verticalOffset = measuredHeight + INFO_WINDOW_GAP_PX;
    } else if (lat - south < verticalMargin) {
      verticalOffset = -(measuredHeight + INFO_WINDOW_GAP_PX);
    }

    const lngSpan = getLngSpan(east, west);
    const horizontalMargin = Math.max(lngSpan * 0.15, MIN_LNG_MARGIN_DEG);
    let horizontalOffset = 0;
    if (getAngularLngDistance(lng, west) < horizontalMargin) {
      horizontalOffset = INFO_WINDOW_GAP_PX;
    } else if (getAngularLngDistance(lng, east) < horizontalMargin) {
      horizontalOffset = -INFO_WINDOW_GAP_PX;
    }

    return [horizontalOffset, verticalOffset];
  }, [infoWindowPlace, viewportBounds, infoWindowHeight]);

  const handleHover = useCallback(
    (placeId: string | null, source: HoverSource = "map") => {
      if (!onPlaceHover) {
        return;
      }
      if (placeId === null) {
        requestHoverClear();
        return;
      }
      clearScheduledHover();
      onPlaceHover(placeId, source);
    },
    [clearScheduledHover, onPlaceHover, requestHoverClear]
  );

  const handleSelect = (placeId: string | null) => {
    onPlaceSelect?.(placeId);
  };

  const handleInfoWindowMouseEnter = useCallback(() => {
    if (infoWindowPlace) {
      handleHover(infoWindowPlace.id);
    }
  }, [infoWindowPlace, handleHover]);

  const handleInfoWindowMouseLeave = useCallback(() => {
    handleHover(null);
  }, [handleHover]);

  if (!apiKey) {
    return (
      <div className="map-placeholder">
        Missing Google Maps API key. Please add REACT_APP_GOOGLE_MAPS_KEY.
      </div>
    );
  }

  const emitViewportChange = (event: MapEvent) => {
    const { map } = event;
    const center = map?.getCenter()?.toJSON();
    if (!center) {
      return;
    }
    if (map && mapInstanceRef.current !== map) {
      mapInstanceRef.current = map;
    }
    if (!isMapReady) {
      setIsMapReady(true);
    }
    const zoom = map.getZoom() ?? 12;
    const bounds = map.getBounds()?.toJSON() ?? null;
    if (bounds) {
      setViewportBounds(bounds);
    }
    if (onViewportChanged) {
      onViewportChanged({
        center,
        bounds,
        zoom,
      });
    }
  };

  return (
    <APIProvider apiKey={apiKey}>
      <div className="map-container">
        <Map
          mapId={process.env.REACT_APP_MAP_ID}
          defaultCenter={initialCenter}
          defaultZoom={10}
          gestureHandling="greedy"
          disableDefaultUI
          zoomControl
          onIdle={emitViewportChange}
        >
          {placesWithLocations.map((place) => {
            const isActive = place.id === activePlaceId;
            return (
              <AdvancedMarker
                key={place.id}
                position={place.location}
                onMouseEnter={() => handleHover(place.id)}
                onMouseLeave={() => handleHover(null)}
                onClick={() => handleSelect(place.id)}
              >
                <Pin
                  background={isActive ? "#2563eb" : "#ffffff"}
                  borderColor={isActive ? "#1d4ed8" : "#94a3b8"}
                  glyphColor={isActive ? "#ffffff" : "#1d4ed8"}
                />
              </AdvancedMarker>
            );
          })}
          {infoWindowPlace && (
            <InfoWindow
              position={infoWindowPlace.location}
              pixelOffset={infoWindowOffset}
              onCloseClick={() => handleSelect(null)}
              disableAutoPan
            >
              <div
                className="map-infowindow__cardWrapper"
                ref={infoWindowContentRef}
                onMouseEnter={handleInfoWindowMouseEnter}
                onMouseLeave={handleInfoWindowMouseLeave}
              >
                <PlaceCard
                  id={infoWindowPlace.id}
                  name={infoWindowPlace.name ?? "Unknown place"}
                  imageUrl={infoWindowPlace.imageUrl}
                  city={infoWindowPlace.city}
                  country={infoWindowPlace.country}
                  saved={infoWindowPlace.saved}
                  isSelected
                  onHover={(placeId) => handleHover(placeId, "map")}
                  onSelect={handleSelect}
                  className="place-card-root--popover"
                />
              </div>
            </InfoWindow>
          )}
        </Map>
      </div>
    </APIProvider>
  );
}
